/**
 * One report in full: vote, body, technical context, media, discussion.
 *
 * Single column — there is no side panel. Status and type sit under the title,
 * and the auto-captured technical context sits after the description, which is
 * where it is actually read.
 *
 * The header is the brand-coloured block, matching TaskDialog: DialogHeader
 * already bleeds to the dialog edges, so it only needs the colour. Sections use
 * plain labels — no "LIITTEET · 2 ————" rules, which nothing else here does.
 */

import { useEffect, useState } from "react";

import { CommentThread } from "@/components/kehitys/CommentThread";
import { MediaGallery } from "@/components/kehitys/MediaGallery";
import {
  StatusTriageSelect,
  WontDoReasonPanel,
  useStatusTriage,
} from "@/components/kehitys/StatusSelect";
import { VoteButton } from "@/components/kehitys/VoteButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useFeedbackReport } from "@/hooks/useFeedbackReport";
import { FEEDBACK_KIND_META, FEEDBACK_STATUS_META } from "@/lib/feedbackMeta";
import { InfoIcon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import type { FeedbackReport } from "@/types";

interface FeedbackDetailProps {
  reportId: string | null;
  onClose: () => void;
  onToggleVote: (id: string) => void;
}

/** The close X sits on the blue header, so it needs to be white — same
 *  treatment TaskDialog gives it. */
const CLOSE_ON_PRIMARY =
  "[&>button]:h-8 [&>button]:w-8 [&>button]:flex [&>button]:items-center " +
  "[&>button]:justify-center [&>button]:text-white [&>button]:opacity-80 " +
  "[&>button]:rounded-md [&>button:hover]:opacity-100 [&>button:hover]:bg-white/15";

export function FeedbackDetail({
  reportId,
  onClose,
  onToggleVote,
}: FeedbackDetailProps) {
  const { dbUser } = useAuth();
  const {
    report: fetched,
    loading,
    addComment,
    setStatus,
    deleteComment,
  } = useFeedbackReport(reportId);

  const isOpen = reportId !== null;

  // Radix keeps DialogContent mounted for its close animation, but reportId is
  // already null by then — so the query is skipped, the report is gone, and
  // without a retained copy the dialog would spend its whole fade-out rendering
  // the "Ilmoitusta ei löytynyt" branch. Read only while closed, so switching
  // reports still shows the skeleton rather than the previous one's content.
  const [lastOpened, setLastOpened] = useState<FeedbackReport | null>(null);
  useEffect(() => {
    if (fetched) setLastOpened(fetched);
  }, [fetched]);
  const report = isOpen ? fetched : lastOpened;

  // Technical context is diagnostic detail, not part of reading the report, so it
  // stays collapsed until asked for. Reset on switching reports, otherwise it
  // would stay open across every report opened afterwards.
  const [showContext, setShowContext] = useState(false);
  useEffect(() => setShowContext(false), [reportId]);

  const mayTriage =
    dbUser?.accessLevel === "ADMIN" || dbUser?.accessLevel === "INTERNAL_PLUS";

  // Called unconditionally — the report is still null on the loading branch,
  // hence the nullable status/version.
  const triage = useStatusTriage({
    status: report?.status ?? null,
    version: report?.version ?? null,
    existingNote: report?.resolutionNote ?? null,
    onChange: setStatus,
  });

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={`max-h-[90vh] max-w-3xl overflow-y-auto border-0 ${CLOSE_ON_PRIMARY}`}
      >
        {loading && !report ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-7 w-2/3" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : !report ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Ilmoitusta ei löytynyt.
          </p>
        ) : (
          <>
            {/* pt-4/pb-3 rather than DialogHeader's default pt-6/pb-4: the
                header carries a title, one metadata line, the vote control and
                the status control, and the default padding left a band of empty
                blue. */}
            <DialogHeader className="border-b-0 bg-primary pb-3 pt-4 text-primary-foreground sm:rounded-t-lg">
              <div className="flex items-start gap-3">
                <VoteButton
                  voteCount={report.voteCount}
                  hasVoted={report.viewerHasVoted}
                  onToggle={() => onToggleVote(report.id)}
                  size="lg"
                  onPrimary
                />
                <div className="min-w-0 flex-1">
                  {/* Only the chip variant needs pr-10 for the close X — when
                      the select is there it holds that corner open itself. */}
                  <DialogTitle
                    className={cn(
                      "text-base leading-snug text-primary-foreground",
                      !mayTriage && "pr-10",
                    )}
                  >
                    {report.title}
                  </DialogTitle>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-primary-foreground/85">
                    {/* Read-only status for everyone who cannot triage — a
                        translucent white, not the list's coloured pill, whose
                        palette is tuned for a light row and several of whose
                        variants vanish against bg-primary. */}
                    {!mayTriage && (
                      <span className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
                        {FEEDBACK_STATUS_META[report.status].label}
                      </span>
                    )}
                    <span>{FEEDBACK_KIND_META[report.kind].label}</span>
                    <span aria-hidden>·</span>
                    <span className="font-mono text-[11px]">{report.reference}</span>
                    <span aria-hidden>·</span>
                    <span>
                      {report.createdBy?.firstName} {report.createdBy?.lastName}
                    </span>
                    <span aria-hidden>·</span>
                    <span>
                      {new Date(report.createdAt).toLocaleDateString("fi-FI")}
                    </span>
                    {report.context && (
                      <button
                        type="button"
                        onClick={() => setShowContext((open) => !open)}
                        aria-expanded={showContext}
                        aria-controls="feedback-technical-context"
                        title="Tekniset tiedot ilmoitushetkellä"
                        className="rounded p-0.5 text-primary-foreground/70 hover:bg-white/15 hover:text-primary-foreground"
                      >
                        <InfoIcon className="h-4 w-4" />
                        <span className="sr-only">
                          Näytä tekniset tiedot ilmoitushetkellä
                        </span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Centred on the close button's line, not merely near it. The
                    X is top-4 h-8 against the dialog, so its centre is 2rem
                    from the top; this row starts at 1rem (header pt-4), so an
                    h-6 pill needs mt-1 to share that centre. mr-8 ends the pill
                    0.5rem before the X's left edge, which the header's own
                    padding does not reach. */}
                {mayTriage && (
                  <StatusTriageSelect
                    triage={triage}
                    className="mr-8 mt-1 shrink-0"
                  />
                )}
              </div>
            </DialogHeader>

            {/* pt-4 is what separates the content from the header block — the
                header bleeds to the dialog edge, so without it the first block
                butts straight against the blue. */}
            <div className="flex flex-col gap-5 pt-4">
              {/* First in the body, directly under the select that opens it. */}
              {mayTriage && <WontDoReasonPanel triage={triage} />}

              {report.description && (
                <p className="max-w-prose whitespace-pre-wrap text-sm">
                  {report.description}
                </p>
              )}

              {report.resolutionNote && (
                <div className="rounded-lg border-l-2 border-primary bg-muted/40 px-3 py-2.5">
                  <span className="text-[10.5px] font-bold uppercase tracking-wider text-primary">
                    Tiimin vastaus
                  </span>
                  <p className="mt-1 whitespace-pre-wrap text-sm">
                    {report.resolutionNote}
                  </p>
                </div>
              )}

              {/* Collapsed by default — revealed by the info icon in the header.
                  Useful when diagnosing, noise when reading. */}
              {report.context && showContext && (
                <div
                  id="feedback-technical-context"
                  className="flex flex-col gap-1.5 rounded-lg border bg-muted/40 px-3 py-2.5"
                >
                  <span className="text-xs font-semibold">
                    Tekniset tiedot ilmoitushetkellä
                  </span>
                  <dl className="grid grid-cols-1 gap-x-3 gap-y-1 font-mono text-[11.5px] text-muted-foreground sm:grid-cols-2">
                    {report.context.path && (
                      <span>
                        Sivu <b className="text-foreground">{report.context.path}</b>
                      </span>
                    )}
                    {report.context.appVersion && (
                      <span>
                        Versio{" "}
                        <b className="text-foreground">{report.context.appVersion}</b>
                      </span>
                    )}
                    {report.context.browser && (
                      <span className="truncate">
                        Selain{" "}
                        <b className="text-foreground">{report.context.browser}</b>
                      </span>
                    )}
                    {report.context.os && (
                      <span>
                        Käyttöjärjestelmä{" "}
                        <b className="text-foreground">{report.context.os}</b>
                      </span>
                    )}
                  </dl>
                </div>
              )}

              {report.attachments.length > 0 && (
                <div className="flex flex-col gap-2.5">
                  <span className="text-xs font-semibold">Liitteet</span>
                  <MediaGallery attachments={report.attachments} />
                </div>
              )}

              <CommentThread
                comments={report.comments ?? []}
                onAdd={addComment}
                onDelete={deleteComment}
              />
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
